import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-hot-toast";
import {
  PlusIcon,
  TrashIcon,
  DocumentIcon,
  EyeSlashIcon,
} from "@heroicons/react/24/outline";
import cn from "classnames";
import Button from "../common/Button";
import { SortState, ViewMode } from "../common/ListViewControls";
import ItemCard from "../common/ItemCard";
import ListPage, { ListColumn } from "../common/ListPage";
import { useLocalStorageState } from "../../hooks/useLocalStorageState";
import { RedactionDocument } from "../../db/schema";

interface RedactionManagerProps {
  documents: RedactionDocument[];
  loading: boolean;
  onRefresh: () => Promise<void>;
  // Opens the upload modal (owned by RedactionsRouter).
  onUploadClick?: () => void;
  // PDFs dropped anywhere on the list; the page opens the upload modal with them.
  onFilesDropped?: (files: File[]) => void;
  // Set by the legacy /redactions/upload route so old links still open the
  // uploader; the route redirects here and the modal opens on mount.
  autoOpenUpload?: boolean;
}

const STATUS_BADGES: Record<string, { label: string; className: string }> = {
  uploaded: { label: "Not redacted", className: "bg-gray-100 dark:bg-base-200 text-gray-600 dark:text-base-content/70" },
  processing: {
    label: "Identifying PII...",
    className: "bg-yellow-100 dark:bg-yellow-950/40 text-yellow-800 dark:text-amber-400",
  },
  ready: { label: "Redacted", className: "bg-green-100 dark:bg-green-950/40 text-green-800 dark:text-green-400" },
  error: { label: "Error", className: "bg-red-100 dark:bg-red-950/40 text-red-800 dark:text-red-400" },
};

// Sort order for the status column: workflow progression
const STATUS_RANK: Record<string, number> = {
  uploaded: 0,
  processing: 1,
  ready: 2,
  error: 3,
};

type SortKey =
  | "name"
  | "status"
  | "redactions"
  | "pages"
  | "created_at"
  | "updated_at";

const enabledRedactionCount = (doc: RedactionDocument) =>
  (doc.redactions || []).filter((r) => r.enabled).length;

const RedactionManager: React.FC<RedactionManagerProps> = ({
  documents,
  loading,
  onRefresh,
  onUploadClick,
  onFilesDropped,
  autoOpenUpload = false,
}) => {
  const navigate = useNavigate();
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [viewMode, setViewMode] = useLocalStorageState<ViewMode>(
    "redactions.viewMode",
    "grid"
  );
  const [sort, setSort] = useLocalStorageState<SortState<SortKey>>(
    "redactions.sort",
    { key: "created_at", dir: "desc" }
  );

  // Open the uploader once for /redactions/upload, then drop back to the
  // canonical list URL so a refresh doesn't reopen it.
  // The modal lives in RedactionsRouter, outside <Routes>, so this navigation
  // can't unmount it.
  useEffect(() => {
    if (!autoOpenUpload || !onUploadClick) return;
    onUploadClick();
    navigate("/redactions", { replace: true });
  }, [autoOpenUpload, onUploadClick, navigate]);

  const filteredDocuments = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return documents;
    return documents.filter((doc) => doc.name.toLowerCase().includes(query));
  }, [documents, search]);

  const sortedDocuments = useMemo(() => {
    const sorted = [...filteredDocuments].sort((a, b) => {
      let cmp = 0;
      switch (sort.key) {
        case "name":
          cmp = a.name.localeCompare(b.name);
          break;
        case "status":
          cmp =
            (STATUS_RANK[a.status || "uploaded"] ?? 0) -
            (STATUS_RANK[b.status || "uploaded"] ?? 0);
          break;
        case "redactions":
          cmp = enabledRedactionCount(a) - enabledRedactionCount(b);
          break;
        case "pages":
          cmp = (a.page_count || 0) - (b.page_count || 0);
          break;
        case "updated_at":
          cmp =
            new Date(a.updated_at || 0).getTime() -
            new Date(b.updated_at || 0).getTime();
          break;
        case "created_at":
        default:
          cmp =
            new Date(a.created_at || 0).getTime() -
            new Date(b.created_at || 0).getTime();
          break;
      }
      return sort.dir === "asc" ? cmp : -cmp;
    });
    return sorted;
  }, [filteredDocuments, sort]);

  const handleDelete = async (doc: RedactionDocument) => {
    if (
      !window.confirm(
        `Delete "${doc.name}"? The document and its redactions will be removed.`
      )
    ) {
      return;
    }
    setDeletingId(doc.id);
    try {
      await axios.delete(`/api/redaction-documents/${doc.id}`);
      toast.success("Document deleted");
      await onRefresh();
    } catch (error) {
      console.error("Error deleting redaction document:", error);
      toast.error("Failed to delete document");
    } finally {
      setDeletingId(null);
    }
  };

  const deleteButton = (doc: RedactionDocument) => (
    <button
      onClick={(e) => {
        e.stopPropagation();
        handleDelete(doc);
      }}
      disabled={deletingId === doc.id}
      className="p-1 text-gray-300 dark:text-base-content/30 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all"
      title="Delete document"
    >
      <TrashIcon className="h-4 w-4" />
    </button>
  );

  const columns: ListColumn<RedactionDocument, SortKey>[] = [
    {
      key: "name",
      header: "Name",
      sortKey: "name",
      render: (doc) => (
        <div className="flex items-center gap-2 min-w-0">
          <DocumentIcon className="h-5 w-5 text-primary flex-shrink-0" />
          <span className="font-medium truncate">{doc.name}</span>
        </div>
      ),
    },
    {
      key: "status",
      header: "Status",
      sortKey: "status",
      render: (doc) => {
        const badge = STATUS_BADGES[doc.status || "uploaded"];
        return (
          <span className={cn("px-2 py-0.5 rounded text-xs font-medium inline-flex items-center gap-1.5 whitespace-nowrap", badge?.className)}>
            {doc.status === "processing" && <span className="loading loading-spinner loading-xs" />}
            {badge?.label}
          </span>
        );
      },
    },
    {
      key: "redactions",
      header: "Redactions",
      sortKey: "redactions",
      defaultDir: "desc",
      className: "text-sm text-base-content/60",
      render: (doc) => (doc.status === "ready" ? enabledRedactionCount(doc) : "—"),
    },
    { key: "pages", header: "Pages", sortKey: "pages", defaultDir: "desc", className: "text-sm text-base-content/60", render: (doc) => doc.page_count || "—" },
    {
      key: "created_at",
      header: "Created",
      sortKey: "created_at",
      defaultDir: "desc",
      className: "text-sm text-base-content/60 whitespace-nowrap",
      render: (doc) => (doc.created_at ? new Date(doc.created_at).toLocaleDateString() : "—"),
    },
    {
      key: "updated_at",
      header: "Modified",
      sortKey: "updated_at",
      defaultDir: "desc",
      className: "text-sm text-base-content/60 whitespace-nowrap",
      render: (doc) => (doc.updated_at ? new Date(doc.updated_at).toLocaleDateString() : "—"),
    },
    { key: "actions", header: "", align: "right", render: deleteButton },
  ];

  return (
    <ListPage<RedactionDocument, SortKey>
      onFilesDropped={onFilesDropped}
      dropMessage="Drop a PDF to redact"
      title="Redactions"
      subtitle="Upload PDFs and automatically redact PII before public release"
      action={
        <Button variant="primary" onClick={onUploadClick}>
          <PlusIcon className="h-5 w-5 mr-1" />
          Upload PDF
        </Button>
      }
      items={sortedDocuments}
      getKey={(doc) => doc.id}
      columns={columns}
      sort={sort}
      onSort={setSort}
      onRowClick={(doc) => navigate(`/redactions/${doc.id}`)}
      rowDisabled={(doc) => doc.status === "processing"}
      search={{ value: search, onChange: setSearch, placeholder: "Search documents..." }}
      viewMode={viewMode}
      onViewModeChange={setViewMode}
      renderCard={(doc) => {
        const isProcessing = doc.status === "processing";
        const badge = STATUS_BADGES[doc.status || "uploaded"];
        const redactionCount = enabledRedactionCount(doc);
        return (
          <ItemCard
            disabled={isProcessing}
            onClick={() => navigate(`/redactions/${doc.id}`)}
            icon={<DocumentIcon className="h-6 w-6 text-primary" />}
            title={doc.name}
            actions={deleteButton(doc)}
            meta={
              <>
                <span
                  className={cn(
                    "px-2 py-0.5 rounded text-xs font-medium inline-flex items-center gap-1.5",
                    isProcessing ? "bg-yellow-100 dark:bg-yellow-950/40 text-yellow-800 dark:text-amber-400" : badge?.className
                  )}
                >
                  {isProcessing && <span className="loading loading-spinner loading-xs" />}
                  {isProcessing ? "Identifying PII..." : badge?.label}
                </span>
                {!isProcessing && doc.status === "ready" && (
                  <span className="text-xs text-base-content/40">
                    {redactionCount} redaction{redactionCount === 1 ? "" : "s"}
                  </span>
                )}
              </>
            }
          >
            <p className="text-xs text-base-content/40 mt-2">
              {doc.page_count ? `${doc.page_count} page${doc.page_count === 1 ? "" : "s"} · ` : ""}
              {doc.created_at ? new Date(doc.created_at).toLocaleDateString() : ""}
            </p>
          </ItemCard>
        );
      }}
      loading={loading}
      isFiltered={Boolean(search.trim())}
      noMatch={`No documents match "${search.trim()}"`}
      empty={{
        icon: <EyeSlashIcon className="h-12 w-12" />,
        title: "No redaction documents yet",
        description: "Upload a PDF to automatically identify and redact PII",
        action: (
          <Button variant="primary" onClick={onUploadClick}>
            <PlusIcon className="h-5 w-5 mr-1" />
            Upload PDF
          </Button>
        ),
      }}
    />
  );
};

export default RedactionManager;
